/**
 * Patient Profile overhaul — the PATIENT_PROFILE report query, reshaped into the patient panel's data.
 *
 * Plan: docs/plans/2026-10-08-patient-profile-overhaul-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * The new Patient Profile page draws the SAME components as the Schedule View patient panel
 * (src/components/PatientPanel/). The panel's data comes from POST /schedule-view/patient, which is a
 * test-pinned copy of 8 of the PATIENT_PROFILE statements, shaped by schedule-view-service
 * lib/patientPayload.js. That service has no prod stack, so the page instead runs the PATIENT_PROFILE
 * query it has always run and shapes it here into the very same object — one set of components, two
 * sources, no second definition of "unscheduled treatment".
 *
 * ── POSITIONS, NOT NAMES ─────────────────────────────────────────────────────────────────────────
 * /runquery returns one array per statement, in the order main-service/sql/patient-profile.js joins
 * them. PatientProfile.vue reads them by index; so does this file, and the order is pinned in ONE
 * place (PROFILE_SETS) and by cypress/node-tests/patient-profile-adapter.test.js against the SQL file.
 *
 * ── A POSITIVE ALLOW-LIST ────────────────────────────────────────────────────────────────────────
 * PATIENT_KEYS / ROW_KEYS are copied from patientPayload.js (drift-guarded by the same test when the
 * sibling repo is checked out). Keys are NEVER renamed: the PHI demo mask works at the axios boundary
 * by Open Dental's own column names (src/utils/phiMask.js), so it has already masked these rows before
 * they reach this file. `Guarantor` is the one addition — the balance alert needs it.
 */

/** Result-set positions of the PATIENT_PROFILE query (main-service/sql/patient-profile.js:437-438). */
export const PROFILE_SETS = [
  'family', 'patient', 'scheduled', 'unscheduledTx', 'broken', 'planned',
  'followups', 'nonRecall', 'lists', 'commlog', 'formSubmissions', 'asap',
];

/** The patient row — schedule-view-service lib/patientPayload.js PATIENT_KEYS, plus Guarantor. */
export const PATIENT_KEYS = [
  'PatNum', 'LName', 'FName', 'Gender', 'Birthdate',
  'HomePhone', 'WorkPhone', 'WirelessPhone', 'Email', 'City', 'State',
  'Premed', 'TxtMsgOk', 'SmsOptOutAction', 'SmsOptOutAt', 'SmsOptOutKeyword',
  'Language', 'EstBalance', 'PatientAmount', 'RecallScheduled',
  'Guarantor',
];

/** One list per panel section — schedule-view-service lib/patientPayload.js ROW_KEYS, verbatim. */
export const ROW_KEYS = {
  scheduled: ['PatNum', 'ProcDescript', 'AptDateTime', 'Provider', 'AppointmentNotes'],
  unscheduledTx: ['ProcCode', 'ToothNum', 'Surf', 'Descript', 'DateTP', 'Provider', 'Priority'],
  broken: ['PatNum', 'Date', 'AdjustProcCode', 'Provider'],
  planned: ['PatNum', 'AptNum', 'ProcDescript', 'Note', 'AptStatus', 'Provider'],
  commlog: ['CommDateTime', 'Note', 'Type', 'Mode_', 'UserName'],
  formSubmissions: ['FormSubmissionIDForWeb', 'DateUpdated', 'Name'],
  asap: ['AptNum', 'AptStatus', 'AppointmentDate', 'ProcDescript', 'AvailableDays', 'AsapNotes', 'Note'],
  // Phase 6 (2026-10-08): the panel's payload carries these too, so both sources hand the view the same rows.
  followups: ['TaskID', 'TaskTypeID', 'TaskTypeDisplayName', 'FollowupDate', 'AssignedToEmpID', 'Overridden',
    'InitialFollowupNote', 'InitialFollowupCreatedOn', 'CreatedAutomatically', 'InitialFollowupDateSet',
    'FollowupStopped', 'EmployeeManuallyAssigned', 'EmployeeName', 'ClaimNum', 'PendingUnscheduledTx',
    'Attempts', 'AttemptDetails', 'FollowupStatusID', 'TeamioFollowupStatusID', 'Status',
    'completed', 'dueToday', 'pastDue'],
  lists: ['ListID', 'TaskTypeID'],
};

function pick(row, keys) {
  const out = {};
  keys.forEach((k) => { out[k] = row && row[k] !== undefined ? row[k] : null; });
  return out;
}

function setAt(resultSets, name) {
  const s = resultSets[PROFILE_SETS.indexOf(name)];
  return Array.isArray(s) ? s : [];
}

/**
 * Shape the raw PATIENT_PROFILE result sets.
 *
 * Returns `{ data, extras }`:
 *   data    the panel's shape — `{ patient, scheduled, unscheduledTx, broken, planned, followups,
 *           lists, commlog, formSubmissions, asap }` — exactly what /schedule-view/patient returns.
 *   extras  `family` (everyone on the account but this patient — page only), plus `followups` and
 *           `belongsToLists`: the same allow-listed rows as data.followups / data.lists.
 *
 * ⛔ Throws when the set count is wrong, as patientPayload.js does: zipping a drifted result would
 * put commlog rows under "formSubmissions" — plausible-looking and wrong.
 *
 * @param {Array<Array<object>>} resultSets  `runReportQuery`'s answer (axios `response.data`)
 * @param {number|string} patientNum         the patient on screen (excluded from `family`)
 */
export function adaptPatientProfileResults(resultSets, patientNum) {
  if (!Array.isArray(resultSets) || resultSets.length < PROFILE_SETS.length) {
    const got = Array.isArray(resultSets) ? resultSets.length : typeof resultSets;
    throw new Error(`PATIENT_PROFILE: expected ${PROFILE_SETS.length} result sets, got ${got}`);
  }

  const patientRow = setAt(resultSets, 'patient')[0];
  const data = { patient: patientRow ? pick(patientRow, PATIENT_KEYS) : null };
  Object.keys(ROW_KEYS).forEach((name) => {
    data[name] = setAt(resultSets, name).map(row => pick(row, ROW_KEYS[name]));
  });

  const self = Number(patientNum);
  const extras = {
    family: setAt(resultSets, 'family').filter(f => f && Number(f.PatNum) !== self),
    followups: data.followups,
    belongsToLists: data.lists,
  };
  return { data, extras };
}
